import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import {
  Button,
  CoreLink,
  Main,
  ToggleGroup,
} from '../../components';
import Page from '../../containers/Page';
import useClasses from '../../hooks/useClasses';
import useRoutes from '../../hooks/useRoutes';
import WarningModal from './WarningModal';

import {
  questions,
  computeP4RiskLevel,
  isEligibleForStudy,
  isScreeningComplete,
  P4_GATE_QUESTION_ID,
  P4_QUESTION_IDS,
  type QuestionId,
  type QuestionOption,
  type WarningMessageId,
} from '../../config/buddyCoScreeningQuestions';

import './style.scss';

export type BuddyCoAnswers = Partial<Record<QuestionId, number>>;

const PageOnboardingBuddyCo: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { logout, joyride, onboarding } = useRoutes();
  const { buildClass } = useClasses('PageOnboardingBuddyCo', 'PageOnboardingBuddyCo--default');

  const [answers, setAnswers] = useState<BuddyCoAnswers>({});
  const [activeWarning, setActiveWarning] = useState<WarningMessageId | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  const questionsToShow = useMemo(() => {
    const answeredQuestionIds = Object.keys(answers) as QuestionId[];

    const lastAnsweredIndex = answeredQuestionIds.length
      ? Math.max(...answeredQuestionIds.map((id) => questions.findIndex((q) => q.id === id)))
      : -1;

    // Skip P4 questions when selfharm_gate = Nee (0)
    const questionIdsToShow = (() => {
      if (lastAnsweredIndex === -1) {
        return [questions[0].id];
      }
      if (answers[P4_GATE_QUESTION_ID] === 0) {
        return answeredQuestionIds;
      }
      const nextQuestion = questions[lastAnsweredIndex + 1];
      return nextQuestion ? [...answeredQuestionIds, nextQuestion.id] : answeredQuestionIds;
    })();

    return questions.filter((question) => questionIdsToShow.includes(question.id));
  }, [answers]);

  const handleSubmit = useCallback(() => {
    if (!isScreeningComplete(answers)) {
      setShowErrors(true);
      return;
    }

    const p4Risk = computeP4RiskLevel(answers);

    if (p4Risk === 'high') {
      setActiveWarning('p4_highrisk');
      return;
    }

    if (p4Risk === 'lower') {
      setActiveWarning('p4_lowerrisk');
      return;
    }

    if (p4Risk === 'minimal') {
      setActiveWarning('p4_minimalrisk');
      return;
    }

    if (isEligibleForStudy(answers)) {
      history.push({ pathname: onboarding.buddyCoConsent, state: { answers } });
    } else {
      history.push(joyride);
    }
  }, [answers, history, joyride, onboarding.buddyCoConsent]);

  const handleWarningClose = useCallback(() => {
    if (activeWarning === 'p4_highrisk' || activeWarning === 'p4_lowerrisk') {
      setActiveWarning(null);
      history.push(joyride);
    } else if (activeWarning === 'p4_minimalrisk') {
      setActiveWarning(null);
      if (isEligibleForStudy(answers)) {
        history.push({ pathname: onboarding.buddyCoConsent, state: { answers } });
      } else {
        history.push(joyride);
      }
    } else {
      setActiveWarning(null);
    }
  }, [activeWarning, answers, history, joyride, onboarding.buddyCoConsent]);

  return (
    <Page
      className={buildClass()}
      hideMenuMain
      hideChrome
      title={t('buddy_co_screening.page_title')}
      headerContent={(
        <CoreLink
          to={logout}
          title={t('buttons.logout')}
          className={buildClass('header-logout')}
        />
      )}
    >
      <Container>
        <div className="row">
          <div className="col-md-8 col-lg-6 offset-md-2 offset-lg-3 py-4 py-md-12">
            <Main className={buildClass('content')}>
              <h1 className={buildClass('title')}>{t('buddy_co_screening.page_title')}</h1>
              <p className={`${buildClass('body')} body--large`}>
                {t('buddy_co_screening.intro')}
              </p>

              <div className={buildClass('questions')}>
                {questionsToShow.map((question, index) => (
                  <ToggleGroup
                    key={question.id}
                    id={`buddy-co-question-${question.id}`}
                    label={`${index + 1}. ${t(question.i18nKey)}`}
                    type={question.type}
                    options={(question.options as QuestionOption[]).map((o) => ({
                      label: t(o.labelKey),
                      value: String(o.value),
                    }))}
                    selectedValue={answers[question.id] !== undefined
                      ? String(answers[question.id])
                      : undefined}
                    onChange={(val) => {
                      const changedQuestion = questions.find((q) => q.id === question.id);
                      if (!changedQuestion) return;

                      const numVal = Number(val);

                      if (changedQuestion.type === 'binary') {
                        const warningId = changedQuestion.options.find(
                          (option) => option.value === numVal,
                        )?.triggersWarning;

                        if (warningId) {
                          setActiveWarning(warningId);
                        }
                      }

                      const newAnswers = { ...answers, [question.id]: numVal };

                      // Clear P4 answers when Q10 changes to Nee
                      if (question.id === P4_GATE_QUESTION_ID && numVal === 0) {
                        P4_QUESTION_IDS.forEach((id) => {
                          delete newAnswers[id];
                        });
                      }

                      setShowErrors(false);
                      setAnswers(newAnswers);
                    }}
                    error={showErrors && answers[question.id] === undefined
                      ? t('buddy_co_screening.validation')
                      : undefined}
                  />
                ))}
              </div>

              <div className={buildClass('submit')}>
                <Button onClick={handleSubmit}>
                  {t('buddy_co_screening.submit')}
                </Button>
              </div>
            </Main>
          </div>
        </div>
      </Container>

      {activeWarning && (
        <WarningModal
          warningId={activeWarning}
          onClose={handleWarningClose}
        />
      )}
    </Page>
  );
};

export default PageOnboardingBuddyCo;
